import { useState, useEffect } from 'react';
import {
    FireExtinguisher, Plus, Filter, Search,
    Building2, Layers, AlertCircle
} from 'lucide-react';
import EquipmentCard from '../components/equipment/EquipmentCard';
import EquipmentForm from '../components/equipment/EquipmentForm';

interface Equipment {
    id: string;
    equipmentNumber: string;
    equipmentType: 'WATER' | 'FOAM' | 'ABC_POWDER' | 'BC_POWDER' | 'CO2' | 'WET_CHEMICAL' | 'CLASS_D';
    capacity: string;
    status: 'ACTIVE' | 'MAINTENANCE' | 'RETIRED';
    locationDescription?: string;
    installationDate: number;
    facilityId: string;
    zoneId?: string;
}

interface Facility {
    id: string;
    name: string;
}

export default function EquipmentPage() {
    const [equipment, setEquipment] = useState<Equipment[]>([]);
    const [facilities, setFacilities] = useState<Facility[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingItem, setEditingItem] = useState<Equipment | null>(null);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [filterFacility, setFilterFacility] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };

            // Parallel fetch
            const [equipmentRes, facilitiesRes] = await Promise.all([
                fetch('https://fire-safety-api.tedguy280.workers.dev/api/equipment', { headers }), // Fetch global list
                fetch('https://fire-safety-api.tedguy280.workers.dev/api/facilities', { headers })
            ]);

            const equipmentData = await equipmentRes.json() as { success: boolean; equipment: Equipment[] };
            const facilitiesData = await facilitiesRes.json() as { success: boolean; facilities: Facility[] };

            if (equipmentData.success) setEquipment(equipmentData.equipment);
            if (facilitiesData.success) setFacilities(facilitiesData.facilities);

        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this equipment?')) return;

        try {
            const res = await fetch(`https://fire-safety-api.tedguy280.workers.dev/api/equipment/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setEquipment(equipment.filter(e => e.id !== id));
            }
        } catch (error) {
            console.error('Failed to delete:', error);
        }
    };

    const handleSave = () => {
        setShowForm(false);
        setEditingItem(null);
        fetchData(); // Refresh list to get updated data
    };

    const filteredEquipment = equipment.filter(item => {
        const matchesSearch = item.equipmentNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.locationDescription?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesFacility = !filterFacility || item.facilityId === filterFacility;
        const matchesType = !filterType || item.equipmentType === filterType;
        const matchesStatus = !filterStatus || item.status === filterStatus;

        return matchesSearch && matchesFacility && matchesType && matchesStatus;
    });

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-8 animate-fadeIn">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-display font-bold text-white mb-2">Equipment Inventory</h1>
                    <p className="text-steel-400">Manage and track all fire safety assets across your facilities.</p>
                </div>
                <button
                    onClick={() => { setEditingItem(null); setShowForm(true); }}
                    className="btn btn-primary flex items-center gap-2 shadow-lg shadow-ember-500/20"
                >
                    <Plus className="w-5 h-5" /> Add Equipment
                </button>
            </div>

            {/* Filters Bar */}
            <div className="card-glass p-4 flex flex-col md:flex-row gap-4 items-center">
                <div className="flex-1 w-full relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-steel-400" />
                    <input
                        type="text"
                        placeholder="Search equipment number or location..."
                        className="input pl-10 w-full"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>
                <div className="flex w-full md:w-auto gap-2 overflow-x-auto pb-2 md:pb-0">
                    <select
                        className="input min-w-[150px]"
                        value={filterFacility}
                        onChange={e => setFilterFacility(e.target.value)}
                    >
                        <option value="">All Facilities</option>
                        {facilities.map(f => (
                            <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                    </select>

                    <select
                        className="input min-w-[140px]"
                        value={filterType}
                        onChange={e => setFilterType(e.target.value)}
                    >
                        <option value="">All Types</option>
                        <option value="WATER">Water</option>
                        <option value="FOAM">Foam</option>
                        <option value="ABC_POWDER">ABC Powder</option>
                        <option value="CO2">CO2</option>
                    </select>

                    <select
                        className="input min-w-[140px]"
                        value={filterStatus}
                        onChange={e => setFilterStatus(e.target.value)}
                    >
                        <option value="">All Statuses</option>
                        <option value="ACTIVE">Active</option>
                        <option value="MAINTENANCE">Maintenance</option>
                        <option value="RETIRED">Retired</option>
                    </select>
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className="flex justify-center py-20">
                    <div className="spinner w-12 h-12 border-4 text-ember-500" />
                </div>
            ) : filteredEquipment.length === 0 ? (
                <div className="text-center py-20 opacity-50">
                    <FireExtinguisher className="w-16 h-16 mx-auto mb-4 text-steel-600" />
                    <h3 className="text-xl font-bold text-white mb-2">No Equipment Found</h3>
                    <p className="text-steel-400">Try adjusting your filters or add new equipment.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredEquipment.map(item => (
                        <EquipmentCard
                            key={item.id}
                            item={item}
                            onEdit={(item) => { setEditingItem(item); setShowForm(true); }}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {/* Modal */}
            {showForm && (
                <EquipmentForm
                    initialData={editingItem}
                    onClose={() => setShowForm(false)}
                    onSave={handleSave}
                />
            )}
        </div>
    );
}
